'use client'

import * as React from 'react'
import Link from 'next/link'
import { Bookmark, Bell, BellOff, CalendarDays, Zap, Lock, Loader2, Search, Globe, Clock, AlertCircle } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Chip, Seg } from '@/components/roostr'
import { useAuth } from '@/components/auth'
import { useSearchStore, useKeywordsStore } from '@/lib/store'
import { api, ApiError } from '@/lib/api'
import { FILTER_TAGS, MAX_SAVED_SEARCHES } from '@/lib/constants'
import { useI18n } from '@/lib/i18n'
import { useSubscription } from '@/lib/subscription'
import { UpgradePrompt } from '@/components/subscription'
import type { SearchCadence } from '@/components/searches'
import type { PremiumFeature, SavedSearchCreate } from '@/lib/types'


interface SaveSearchDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSaved?: () => void
}

export function SaveSearchDialog({ open, onOpenChange, onSaved }: SaveSearchDialogProps) {
  const { user } = useAuth()
  const { query, filters, activeTags, sort, travelTime, totalCount } = useSearchStore()
  const { filterTags } = useKeywordsStore()
  const { t } = useI18n()
  const { canUse } = useSubscription()

  const [name, setName] = React.useState('')
  const [cadence, setCadence] = React.useState<SearchCadence>('off')
  const [isSaving, setIsSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [showUpgradePrompt, setShowUpgradePrompt] = React.useState(false)
  const [upgradeFeature, setUpgradeFeature] = React.useState<PremiumFeature>('extra_roostrs')
  // How many saved searches the user already has, fetched when the dialog opens
  // (the list is capped at MAX_SAVED_SEARCHES so this is a tiny request). `null`
  // = unknown/not-yet-loaded or fetch failed → we fail open and allow the save.
  const [savedCount, setSavedCount] = React.useState<number | null>(null)
  const nameInputRef = React.useRef<HTMLInputElement>(null)

  const atLimit = savedCount !== null && savedCount >= MAX_SAVED_SEARCHES
  const instantLocked = !canUse('instant_notifications')
  const limitMessage = t(
    'search.searchLimitReached',
    "You've reached the {n} saved-search limit — delete one to add another."
  ).replace('{n}', String(MAX_SAVED_SEARCHES))

  const tagLabel = React.useCallback(
    (tagId: string) => {
      const dynamic = filterTags.find((f) => f.id === tagId)
      if (dynamic) return dynamic.label
      const stat = FILTER_TAGS.find((f) => f.id === tagId)
      return stat?.label || tagId
    },
    [filterTags]
  )

  React.useEffect(() => {
    if (open) {
      let defaultName = ''

      if (query) {
        defaultName = query.length > 40 ? query.substring(0, 40) + '...' : query
      } else if (activeTags.length > 0) {
        const tagLabels = activeTags.slice(0, 3).map(tagLabel)
        defaultName = tagLabels.join(', ')
        if (activeTags.length > 3) {
          defaultName += ` +${activeTags.length - 3}`
        }
      } else {
        defaultName = `${t('search.saveSearchTitle', 'Search')} ${new Date().toLocaleDateString()}`
      }

      setName(defaultName)
      setCadence('off')
      setError(null)
      // Focus + select the suggested name (design search.jsx:268)
      requestAnimationFrame(() => {
        nameInputRef.current?.focus()
        nameInputRef.current?.select()
      })
    }
  }, [open, query, activeTags, t, tagLabel])

  // Count existing saved searches so we can enforce the cap. Refetched each time
  // the dialog opens so the count reflects any deletes made on /searches.
  React.useEffect(() => {
    if (!open || !user) {
      setSavedCount(null)
      return
    }
    let cancelled = false
    api
      .getSavedSearches()
      .then((list) => {
        if (!cancelled) setSavedCount(list.length)
      })
      .catch(() => {
        // Fail open — a transient count fetch error shouldn't block a save.
        if (!cancelled) setSavedCount(null)
      })
    return () => {
      cancelled = true
    }
  }, [open, user])

  const handleSave = async () => {
    if (!user) {
      setError(t('search.signInToSave', 'Please sign in to save searches'))
      return
    }

    if (!name.trim()) {
      setError(t('search.enterSearchName', 'Please enter a name for this search'))
      return
    }

    if (atLimit) {
      setError(limitMessage)
      return
    }

    setIsSaving(true)
    setError(null)

    try {
      const alertsOn = cadence !== 'off'
      const searchToSave: SavedSearchCreate = {
        name: name.trim(),
        query: query || undefined,
        filters: Object.keys(filters).length > 0 ? filters : undefined,
        tags: activeTags.length > 0 ? activeTags : undefined,
        travel_time: travelTime || undefined,
        sort: sort || 'relevance',
        is_active: alertsOn,
        // off keeps the search paused (backend default cadence); daily/instant
        // write the user's pick — the field the notification worker reads.
        cadence: alertsOn ? cadence : undefined,
      }

      await api.createSavedSearch(searchToSave)
      // Refresh the header's saved-searches badge (saved-searches-button.tsx).
      window.dispatchEvent(new CustomEvent('saved-searches-changed'))

      onOpenChange(false)
      onSaved?.()
    } catch (err) {
      console.error('Error saving search:', err)
      if (err instanceof ApiError) {
        if (err.isUnauthorized) {
          setError(t('search.signInToSave', 'Please sign in to save searches'))
        } else if (err.isSubscriptionRequired) {
          // Close this dialog and show the upgrade modal instead
          setUpgradeFeature('extra_roostrs')
          onOpenChange(false)
          setShowUpgradePrompt(true)
        } else {
          setError(err.message || t('search.failedToSave', 'Failed to save search. Please try again.'))
        }
      } else {
        setError(t('search.failedToSave', 'Failed to save search. Please try again.'))
      }
    } finally {
      setIsSaving(false)
    }
  }

  // Instant is Penthouse-only — clicking it while locked routes to the upgrade
  // prompt (mirrors the saved-search card's Seg) rather than selecting it. The
  // save dialog closes first so two Radix dialogs never stack (same pattern as
  // the roostr-cap 403 in handleSave).
  const handleCadenceSelect = (value: SearchCadence) => {
    if (value === 'instant' && instantLocked) {
      setUpgradeFeature('instant_notifications')
      onOpenChange(false)
      setShowUpgradePrompt(true)
      return
    }
    setCadence(value)
  }

  const microLabel = 'mb-[7px] text-[10.5px] font-bold uppercase tracking-[0.6px] text-ink-muted'

  return (
    <>
    <UpgradePrompt
      open={showUpgradePrompt}
      onOpenChange={setShowUpgradePrompt}
      feature={upgradeFeature}
    />
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[460px] max-w-full rounded-shape-xl border-0 p-[24px_26px] shadow-e3">
        <DialogHeader className="space-y-1 text-left">
          <DialogTitle className="text-[20px] font-bold tracking-[-0.3px]">
            {t('search.saveSearchTitle', 'Save this search')}
          </DialogTitle>
          <DialogDescription className="text-[13px] text-ink-secondary">
            {t('search.saveSearchDescription', "Roostr will watch the market and file new matches into What's new.")}
          </DialogDescription>
        </DialogHeader>

        {atLimit && (
          <div className="mt-3 flex items-start gap-2.5 rounded-shape-sm bg-primary/10 px-[13px] py-[11px]">
            <AlertCircle className="mt-px h-4 w-4 flex-shrink-0 text-primary" />
            <div className="min-w-0 text-[12.5px]">
              <p className="font-semibold text-ink">{limitMessage}</p>
              <Link
                href="/searches"
                onClick={() => onOpenChange(false)}
                className="mt-1 inline-block font-semibold text-primary underline-offset-2 hover:underline"
              >
                {t('search.manageSearches', 'Manage searches')}
              </Link>
            </div>
          </div>
        )}

        <div className="mt-2 space-y-4">
          {/* Name input — design search.jsx:282-292 */}
          <div>
            <div className={microLabel}>{t('search.searchName', 'Name')}</div>
            <input
              ref={nameInputRef}
              id="search-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && name.trim() && !isSaving) handleSave()
              }}
              placeholder={t('search.searchNamePlaceholder', 'e.g. Sunny 2+kk near the park')}
              disabled={isSaving}
              className="w-full rounded-shape-sm border border-outline bg-surface-low px-3.5 py-[11px] text-sm font-semibold outline-none focus:border-primary"
            />
          </div>

          {/* What it watches — design search.jsx:294-302 */}
          <div>
            <div className={microLabel}>{t('search.whatItWatches', 'What it watches')}</div>
            <div className="flex flex-wrap gap-1.5">
              {query && <Chip icon={<Search className="h-3.5 w-3.5" />} label={`“${query}”`} />}
              {activeTags.map((tagId) => (
                <Chip key={tagId} label={tagLabel(tagId)} />
              ))}
              {travelTime && (
                <Chip
                  icon={<Clock className="h-3.5 w-3.5" />}
                  label={`${travelTime.minutes} min ${travelTime.mode} · ${travelTime.origin}`}
                />
              )}
              {!query && activeTags.length === 0 && !travelTime && (
                <Chip icon={<Globe className="h-3.5 w-3.5" />} label={t('search.allOfCity', 'All of Praha')} />
              )}
            </div>
            <div className="mt-2 text-xs text-ink-muted">
              {totalCount.toLocaleString('cs-CZ')} {t('search.matchesRightNow', 'matches right now')}
            </div>
          </div>

          {/* Alerts cadence — mirrors the saved-search card's Off/Daily/Instant
              Seg (user-directed 2026-07-16, replaces the on/off toggle). off →
              is_active false; daily/instant write the search's cadence. Instant
              is Penthouse-gated (lock + upgrade prompt) like the card, with the
              server 403 as backstop. */}
          <div className="rounded-shape-sm bg-surface-low px-[13px] py-[11px]">
            <div className="flex items-start gap-[10px]">
              <Bell className="mt-px h-4 w-4 flex-shrink-0 text-ink-secondary" />
              <div className="min-w-0 flex-1">
                <p className="text-[12.5px] font-medium text-ink-secondary">
                  {t('search.alertsLabel', 'Alerts')}
                </p>
                <p className="text-[11px] text-ink-muted">
                  {cadence === 'off'
                    ? t('search.cadenceOffHint', "No alerts — new matches still collect in What's new.")
                    : cadence === 'daily'
                      ? t('searches.alertsToastDaily', 'One digest every morning')
                      : t('searches.alertsToastInstant', 'Alerts the moment a match lands')}
                </p>
              </div>
            </div>
            <Seg<SearchCadence>
              compact
              className="mt-[10px]"
              value={cadence}
              onChange={handleCadenceSelect}
              options={[
                {
                  value: 'off',
                  label: t('searches.alertsOff', 'Off'),
                  icon: <BellOff size={12} />,
                  activeClassName: 'text-ink-muted',
                },
                {
                  value: 'daily',
                  label: t('searches.alertsDaily', 'Daily'),
                  icon: <CalendarDays size={12} />,
                },
                {
                  value: 'instant',
                  label: t('searches.alertsInstant', 'Instant'),
                  icon: instantLocked ? <Lock size={12} /> : <Zap size={12} />,
                  activeClassName: 'bg-primary-container text-on-primary-container',
                },
              ]}
            />
          </div>

          {/* Error message */}
          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}
        </div>

        {/* Footer — design search.jsx:311-317 */}
        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
            className="rounded-full px-4 py-[10px] text-[13.5px] font-semibold text-ink-secondary transition-transform active:scale-[0.97]"
          >
            {t('search.cancel', 'Cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || !name.trim() || atLimit}
            className="inline-flex items-center gap-1.5 rounded-full bg-primary px-5 py-[10px] text-[13.5px] font-semibold text-white shadow-e1 transition-transform active:scale-[0.97] disabled:opacity-50"
          >
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('search.saving', 'Saving...')}
              </>
            ) : (
              <>
                <Bookmark className="h-4 w-4" />
                {t('search.saveSearch', 'Save search')}
              </>
            )}
          </button>
        </div>
      </DialogContent>
    </Dialog>
    </>
  )
}
